import { type z } from "zod";

import {
  AuctionFormatScalarSchema,
  refineAuctionFormat,
} from "@/.zenstack/zod/models/AuctionFormat.schema";
import {
  ForLeaseScalarSchema,
  refineForLease,
} from "@/.zenstack/zod/models/ForLease.schema";

/**
 * Error-only schema for Auction Scheduling (blocking validation)
 *
 * Contains only required field validation and critical business rules from zmodel.
 * This validation blocks form submission.
 */
export const AuctionSchedulingErrorSchema = refineAuctionFormat(
  AuctionFormatScalarSchema.pick({
    auctionStartDate: true,
    auctionEndDate: true,
    startingBid: true,
    reserveBidAmount: true,
    bidIncrement: true,
  }),
);

/**
 * Warning-only schema for Auction Scheduling (non-blocking recommendations)
 *
 * Contains UX recommendations that don't block submission.
 * These are shown immediately to guide users toward best practices.
 */
export const AuctionSchedulingWarningSchema = AuctionFormatScalarSchema.pick({
  auctionStartDate: true,
  auctionEndDate: true,
  startingBid: true,
  reserveBidAmount: true,
  bidIncrement: true,
})
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Recommend longer auction duration (minimum 3 days, optimal 7 days)
    if (data.auctionStartDate && data.auctionEndDate) {
      const durationDays = Math.ceil(
        (data.auctionEndDate.getTime() - data.auctionStartDate.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      if (durationDays < 3) {
        ctx.addIssue({
          code: "custom",
          message:
            "Auction duration should be at least 3 days, with 7 days being optimal for better participation and engagement",
          severity: "warning",
          path: ["auctionEndDate"],
        });
      }
    }
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Recommend competitive pricing
    if (data.startingBid) {
      const startingBidValue = Number(data.startingBid.toString());
      if (startingBidValue > 4000) {
        ctx.addIssue({
          code: "custom",
          message:
            "Your starting bid appears high. Consider researching comparable properties in your area to ensure competitive pricing that attracts bidders.",
          severity: "warning",
          path: ["startingBid"],
        });
      }
    }
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Recommend setting a reserve bid amount
    if (data.reserveBidAmount === null || data.reserveBidAmount === undefined) {
      ctx.addIssue({
        code: "custom",
        message:
          "Consider setting a reserve bid amount that matches your target rent. Setting your starting bid below market price drives engagement.",
        severity: "warning",
        path: ["reserveBidAmount"],
      });
    }
  });

export type AuctionSchedulingFormData = z.infer<
  typeof AuctionSchedulingErrorSchema
>;
export type LeaseSchedulingFormData = z.infer<
  typeof LeaseSchedulingErrorSchema
>;

/**
 * Error-only schema for Lease Scheduling (blocking validation)
 *
 * Contains only required field validation and critical business rules from zmodel.
 * This validation blocks form submission.
 */
export const LeaseSchedulingErrorSchema = refineForLease(
  ForLeaseScalarSchema.pick({
    availableFrom: true,
    leaseTermMonths: true,
  }),
);

/**
 * Warning-only schema for Lease Scheduling (non-blocking recommendations)
 *
 * Contains UX recommendations that don't block submission.
 * These are shown immediately to guide users toward best practices.
 */
export const LeaseSchedulingWarningSchema = ForLeaseScalarSchema.pick({
  availableFrom: true,
  leaseTermMonths: true,
}).superRefine((data, ctx) => {
  // CLIENT WARNING: Recommend longer lease terms
  const leaseTermMonths = data.leaseTermMonths as number;

  if (leaseTermMonths < 12) {
    ctx.addIssue({
      code: "custom",
      message:
        "Short-term leases may limit your tenant pool. Consider offering a 12-month lease term for more stable tenants and reduced turnover costs.",
      severity: "warning",
      path: ["leaseTermMonths"],
    });
  }

  // CLIENT WARNING: Very short-term lease legal requirements
  if (leaseTermMonths < 6) {
    ctx.addIssue({
      code: "custom",
      message:
        "Short-term leases (under 6 months) may have different legal requirements in your area. Please verify local regulations.",
      severity: "warning",
      path: ["leaseTermMonths"],
    });
  }
});
